import { HoverableButton, LayoutType, PlayerContainer, ReactIcons, Text } from "@kalyzee/escaet-ui";
import { MeetingSessionOvenMediaReceiver, MeetingSessionOvenMediaReceiverManagerBaseData } from "@kalyzee/kast-web-sdk";
import { useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { OvenMediaReceiverWebRTCSession } from "@kalyzee/kast-web-sdk/webrtc";
import { useEffect, useMemo, useState } from "react";
import { usePageContext } from "../../../../../../components/navigation/PageContext";
import { MeetingPlayerTriLayoutContainer } from "../../../../../../components/player/container/MeetingPlayerTriLayoutContainer";
import { PlayerItem } from "../../../../../../components/player/interfaces";
import MeetingSessionMediaPlayer, { MeetingSessionMediaPlayerProps } from "../../../../../../components/player/MeetingSessionMediaPlayer";
import { LoadingView } from "../../../../../../components/utils/LoadingView";
import { useMeetingContainerAPI, useMeetingSelector } from "../../../../../../hooks/meeting";
import { getExpertView, useActivityData, useExpertAvailableViews, usePopulatedMeetingData, useStudentAvailableViews } from "../../../../../../hooks/populatedData";
import { useUser } from "../../../../../../store/session/hooks";
import { SessionMode } from "../../../../../../store/session/slices";
import { usePedagogyContext } from "../../../context";
import styles from "./dashboard.module.css";
import { MeetingSessionHighlightingState } from "@kalyzee/kast-web-sdk/websocket";
import { getExpertAudioValidator } from "../../../../../../helpers/audio";
import { expertWebcamName } from "../../../../../../helpers/webcam";

export const MeetingPedagogyInterventionDashboardPage = () => {
  const pageContext = usePageContext<SessionMode.MASTER>();
  const pageContextRef = useRefAutoUpdate(pageContext);
  const meetingContainerAPI = useMeetingContainerAPI(pageContextRef.current?.params?.id);
  const pedagogyCtx = usePedagogyContext();
  const pedagogyCtxRef = useRefAutoUpdate(pedagogyCtx);

  const selfUser = useUser();
  const [meeting] = useMeetingSelector(meetingContainerAPI, (state) => state.meeting);

  const populatedMeetingData = usePopulatedMeetingData(meetingContainerAPI);
  const activityData = useActivityData(populatedMeetingData);
  const availableViews = useExpertAvailableViews(populatedMeetingData, activityData, selfUser?.id);

  const currentView = useMemo(() => {
    const highlightView = availableViews.find((view) => view.type === "highlight");
    if (highlightView) return highlightView;
    return availableViews[availableViews.length - 1]; // TODO : there should be a default view, like this structure : { availableViews, defaultView, currentView }
  }, [availableViews]);

  const enabledVideoSessions = useMemo(
    () =>
      (currentView?.experts ?? [])
        .flatMap((data) => data.session.videoSessions)
        .filter(
          (videoSession) =>
            videoSession.data.mediaSession.name !== expertWebcamName &&
            !pedagogyCtx?.disabledExpertMediaSessionIds?.find((id) => id === videoSession.data.mediaSession?.id)
        ),
    [currentView, pedagogyCtx?.disabledExpertMediaSessionIds]
  );

  const playerItems = useMemo(
    () =>
      enabledVideoSessions.map(
        (session) =>
          new PlayerItem({
            type: "video_session",
            videoSession: session,
          })
      ),
    [enabledVideoSessions, pedagogyCtx?.disabledExpertMediaSessionIds]
  );

  const [focusedVideoSessionId, setFocusedVideoSessionId] = useState<string>();

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  // ---------------------------------------------------------------------- //
  // ---------------------------   EFFECT ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    pedagogyCtxRef.current?.setDisplayRemoteController(true); // display by default
  }, []);

  const view = useMemo(() => {
    return getExpertView("normal", populatedMeetingData, activityData, pedagogyCtx?.spiedGroupId)
  }, [populatedMeetingData, activityData, pedagogyCtx?.spiedGroupId]);

  useEffect(() => {
    const audioValidator = getExpertAudioValidator(view);
    const validator = pedagogyCtxRef.current?.addAudioValidator(audioValidator);
    return () => {
      validator?.flush();
    };
  }, [view]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  if (!meeting) return <LoadingView />;

  const renderPlayerHeader = (session: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>, focusMode: boolean) => {
    const meetingSession = session.data.session;
    const isPedagogy = meetingSession.extra?.isPedagogy;
    const username = meetingSession.user?.username ?? "";
    let text = username;
    if (isPedagogy) {
      text = `${username} (Pédagogie)`;
    } else if (meetingSession?.highlighting === MeetingSessionHighlightingState.ONGOING) {
      text = `${username} (Au tableau)`;
    }
    const renderUsername = () => (
      <Text
        style={{
          fontWeight: 800,
          fontSize: focusMode ? "1.2em" : "0.8em",
          backgroundColor: isPedagogy ? "#f9af5c" : "white",
          borderRadius: "0.25em",
          padding: "0.25em 0.5em",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
        value={text}
      />
    );

    const renderFullScreenButton = () =>
      focusedVideoSessionId ? (
        <ReactIcons.CloseIconBlackBg onClick={() => setFocusedVideoSessionId(undefined)} style={{ width: "2em", height: "2em", cursor: "pointer" }} />
      ) : (
        <HoverableButton.FullScreen onClick={() => setFocusedVideoSessionId(session.getId())} style={{ width: "2em", height: "2em" }} />
      );

    if (focusMode) {
      return (
        <div className={styles.playerHeaderContainer} style={{ backgroundColor: "white", padding: "0.3em" }}>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "1em",
              alignItems: "center",
            }}
          >
            <Text
              style={{
                color: "white",
                fontWeight: 800,
                backgroundColor: "#05C496",
                padding: "0.5em",
                borderRadius: "0.25em",
              }}
              value={"Mode focus".toUpperCase()}
            />
            {renderUsername()}
          </div>
          {renderFullScreenButton()}
        </div>
      );
    }

    return (
      <div className={styles.playerHeaderContainer}>
        {renderUsername()}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "1em",
          }}
        >
          {renderFullScreenButton()}
        </div>
      </div>
    );
  };

  const renderPlayer = (
    session: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>,
    style?: React.CSSProperties,
    focusMode = false,
    playerProps?: MeetingSessionMediaPlayerProps
  ) => (
    <PlayerContainer.Default
      containerStyle={{
        backgroundColor: "#9E9D9B",
        overflow: "hidden",
        borderRadius: "0.5em",
        ...(style ?? {}),
      }}
      key={`ovenmedia-session-${session.getId()}-player-container`}
      overlay={focusMode ? "none" : "hover"}
      headerContent={renderPlayerHeader(session, focusMode)}
    >
      <MeetingSessionMediaPlayer
        style={{ width: "100%", height: "100%", maxHeight: "100%" }}
        {...(playerProps ?? {})}
        meeting={meeting}
        sessionId={session.data.session.id}
        mediaSessionId={session.data.mediaSession?.id!}
        ovenMediaSession={session}
        zoomPanPinch={focusMode}
        muted={true}
        callbacks={{
          playingSound: (v) => {
            playerProps?.callbacks?.playingSound?.(v);
          },
        }}
      />
    </PlayerContainer.Default>
  );

  const renderFocusContent = () => {
    if (!focusedVideoSessionId) return null;
    const focusedVideoSession = enabledVideoSessions.find((s) => s.getId() === focusedVideoSessionId);
    if (!focusedVideoSession) return null;
    return (
      <div
        style={{
          position: "fixed",
          display: "flex",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 9999,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "100%",
            padding: "10px",
            borderRadius: "1em",
            overflow: "hidden",
          }}
        >
          {renderPlayer(
            focusedVideoSession,
            {
              minHeight: 0,
              height: "100%",
              borderTopRightRadius: "0.5em",
              borderTopLeftRadius: "0.5em",
            },
            true
          )}
        </div>
      </div>
    );
  };

  const renderNewLayoutContent = () => (
    <MeetingPlayerTriLayoutContainer
      meeting={meeting}
      items={playerItems}
      layoutProps={{ square: { noPages: true } }}
      renderItem={(rendererProvider, item) => {
        const itemData = item.getData();
        if (itemData.type !== "video_session") return rendererProvider(); // should not happen
        return renderPlayer(itemData.videoSession);
      }}
      layout={pedagogyCtx?.layout ?? LayoutType.Square}
    />
  );

  return (
    <div className={styles.container}>
      {renderNewLayoutContent()}
      {renderFocusContent()}
    </div>
  );
};
